'use client'
import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { ClickAwayListener } from '@mui/material'
import { useTranslations } from 'next-intl'

import { DesktopMultiCitySearchForm } from './DesktopMultiCitySearchForm'

interface DesktopMultiCitySearchFormProps {
    searchManager: ReturnType<typeof import('../../hooks/useHomePageSearchManager').useHomePageSearchManager>
    showError: (message: string) => void
}

const DesktopMultiCitySearchFormWrapper: React.FC<DesktopMultiCitySearchFormProps> = ({ searchManager, showError }) => {
    const {
        flightClassState,
        travelersState,
        handleTravelerChange,
        handleFlightClassChange,
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,
        multiCitySegments,
        handleMultiCitySegmentUpdate,
        handleMultiCitySwap,
        handleAddMultiCitySegment,
        handleRemoveMultiCitySegment,
        getAirportsForMultiCitySegment,
        performMultiCitySearch,
        canAddMoreSegment,
        showRemoveSegment
    } = searchManager

    const t = useTranslations('common')

    // Active component state
    type ActiveComponent = 'from' | 'to' | 'date' | 'travelers' | null
    const [activeComponent, setActiveComponent] = useState<ActiveComponent>(null)

    // Active segment and field state
    const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(null)
    const [activeSegmentField, setActiveSegmentField] = useState<'from' | 'to' | 'date' | null>(null)

    // Compute errors as derived value (no state needed) - only depends on segments
    const segmentsWithSameAirportError = useMemo(() => {
        const errorSegmentIndices = new Set<number>()
        multiCitySegments.forEach((segment, index) => {
            const fromCode = segment.from.airportCode
            const toCode = segment.to.airportCode
            if (fromCode && toCode && fromCode === toCode) {
                errorSegmentIndices.add(index)
            }
        })
        return errorSegmentIndices
    }, [multiCitySegments])

    // Get minDate for each segment - similar to mobile implementation
    // For Segment 1 (index 0): no restrictions
    // For Segment 2+ (index 1+): use previous segment's date
    const getMinDateForSegment = useCallback((segmentIndex: number): Date | null => {
        if (segmentIndex === 0) {
            return null // Segment 1: no restrictions
        }
        
        // For segment 2+ (index 1+), use previous segment's date
        // Segment 2 (index 1): use Segment 1's date
        // Segment 3 (index 2): use Segment 2's date if available, otherwise Segment 1's date
        // And so on...
        for (let i = segmentIndex - 1; i >= 0; i--) {
            if (multiCitySegments[i]?.date) {
                return multiCitySegments[i].date
            }
        }
        return null
    }, [multiCitySegments])

    // Show error toast only when error count changes (not on every segment update)
    useEffect(() => {
        if (segmentsWithSameAirportError.size > 0) {
            showError(t('sameAirportError'))
        }
    }, [segmentsWithSameAirportError.size, showError])

    // Centralized function to handle component activation
    const handleComponentActivation = useCallback((component: ActiveComponent) => {
        if (activeComponent === component && component !== 'from' && component !== 'to') {
            setActiveComponent(null)
        } else {
            setActiveComponent(component)
        }
    }, [activeComponent])

    // Function to close any active component (travelers only)
    const closeActiveComponent = useCallback(() => {
        setActiveComponent(null)
    }, [])
    
    // Function to close active segment component (segment fields)
    const closeActiveSegmentComponent = useCallback(() => {
        setActiveComponent(null)
        setActiveSegmentIndex(null)
        setActiveSegmentField(null)
    }, [])
    
    // Helper function to clear all active state
    const clearActiveState = useCallback(() => {
        setActiveComponent(null)
        setActiveSegmentIndex(null)
        setActiveSegmentField(null)
    }, [])
    
    // Helper function to check if click is inside portal/container (shared logic)
    const isClickInsidePortal = useCallback((target: HTMLElement): boolean => {
        return !!(
            target.closest('[data-search-container]') || 
            target.closest('.MuiMenu-root') || 
            target.closest('.MuiPaper-root') ||
            target.closest('.MuiPopper-root')
        )
    }, [])
    
    // Handle click away for segment 0 (travelers or segment 0 fields)
    const handleSegment0ClickAway = useCallback((event: Event | React.SyntheticEvent) => {
        const target = event.target as HTMLElement
        
        // Don't close if clicking inside portals/containers
        if (isClickInsidePortal(target)) {
            return
        }
        
        // Only close segment 0's active state (travelers when activeSegmentIndex is null, or segment 0 fields when activeSegmentIndex is 0)
        const isSegment0Active = activeSegmentIndex === null || activeSegmentIndex === 0
        if (isSegment0Active) {
            if (activeSegmentIndex === 0) {
                clearActiveState()
            } else {
                setActiveComponent(null) // Only travelers is active
            }
        }
    }, [activeSegmentIndex, isClickInsidePortal, clearActiveState])
    
    // Create click-away handler for a specific segment index
    // Each segment only closes its own active state, not others
    const createSegmentClickAwayHandler = useCallback((segmentIndex: number) => {
        return (event: Event | React.SyntheticEvent) => {
            const target = event.target as HTMLElement
            
            // Don't close if clicking inside portals/containers
            if (isClickInsidePortal(target)) {
                return
            }
            
            // Only close this specific segment's active state
            // Don't close if a different segment is active
            if (activeSegmentIndex === segmentIndex) {
                clearActiveState()
            }
        }
    }, [activeSegmentIndex, isClickInsidePortal, clearActiveState])

    // Handle segment field activation
    const handleSegmentFieldActivation = useCallback((segmentIndex: number, field: 'from' | 'to' | 'date') => {
        setActiveSegmentIndex(segmentIndex)
        setActiveSegmentField(field)
        setActiveComponent(field) // field is already 'from', 'to', or 'date'
    }, [])

    // Handle segment field select (airport or date) - consolidated handler
    const handleSegmentFieldSelect = useCallback((segmentIndex: number, field: 'from' | 'to' | 'date', value: any) => {
        if (field === 'date') {
            // For date, value is already a Date | null (extracted from dates.from in handleMultiCityDateSelect)
            handleMultiCitySegmentUpdate(segmentIndex, 'date', value)
        } else {
            handleMultiCitySegmentUpdate(segmentIndex, field, value)
        }
        clearActiveState()
    }, [handleMultiCitySegmentUpdate, clearActiveState])

    // Handle airport select for segments (kept for backward compatibility with component)
    const handleMultiCityAirportSelect = useCallback((segmentIndex: number, value: string, action: 'from' | 'to') => {
        handleSegmentFieldSelect(segmentIndex, action, value)
    }, [handleSegmentFieldSelect])

    // Handle date select for segments - similar to mobile implementation
    // dates is an object with { from: Date, to: Date } - we only need 'from' for multi-city
    const handleMultiCityDateSelect = useCallback((segmentIndex: number, dates: any, range: any) => {
        // Extract the selected date from dates.from (similar to mobile which passes Date | null directly)
        const selectedDate = dates?.from || null
        handleMultiCitySegmentUpdate(segmentIndex, 'date', selectedDate)
        clearActiveState()
    }, [handleMultiCitySegmentUpdate, clearActiveState])

    // Check if a segment field is active
    const isSegmentFieldActive = useCallback((segmentIndex: number, field: 'from' | 'to' | 'date') => {
        return activeSegmentIndex === segmentIndex && activeSegmentField === field && activeComponent === field
    }, [activeSegmentIndex, activeSegmentField, activeComponent])

    // Check if a component is active
    const isComponentActive = useCallback((component: ActiveComponent) => {
        return activeComponent === component
    }, [activeComponent])

    // Handle swap for specific segment
    const handleSegmentSwap = useCallback((segmentIndex: number) => {
        clearActiveState()
        handleMultiCitySwap(segmentIndex)
    }, [handleMultiCitySwap, clearActiveState])

    // Handle search
    const handleSearch = useCallback(() => {
        // Don't search if there's a validation error (same airport)
        if (segmentsWithSameAirportError.size > 0) {
            showError(t('sameAirportError'))
            return
        }

        // Check for empty fields
        const hasEmptyFields = multiCitySegments.some(
            segment => !segment.from.airportCode || !segment.to.airportCode || !segment.date
        )

        if (hasEmptyFields) {
            showError(t('pleaseFillAllRequiredFields'))
            return
        }

        clearActiveState()
        performMultiCitySearch()
    }, [segmentsWithSameAirportError, multiCitySegments, showError, performMultiCitySearch, clearActiveState, t])

    return (
            <DesktopMultiCitySearchForm
                segments={multiCitySegments}
                segmentsWithSameAirportError={segmentsWithSameAirportError}
                travelersState={travelersState}
                flightClass={Array.isArray(flightClassState) ? flightClassState[0] : flightClassState}
                canAddMoreSegment={canAddMoreSegment}
                showRemoveSegment={showRemoveSegment}
                onSegmentFieldActivation={handleSegmentFieldActivation}
                onAirportSelect={(segmentIndex, value, action) => handleMultiCityAirportSelect(segmentIndex, value, action)}
                onDateSelect={(segmentIndex, dates, range) => handleMultiCityDateSelect(segmentIndex, dates, range)}
                onSwap={handleSegmentSwap}
                onAddMore={handleAddMultiCitySegment}
                onRemove={(segmentIndex) => {
                    handleRemoveMultiCitySegment(segmentIndex)
                }}
                onSearch={handleSearch}
                getAirportsForSegment={getAirportsForMultiCitySegment}
                isSegmentFieldActive={isSegmentFieldActive}
                isComponentActive={isComponentActive}
                handleComponentActivation={handleComponentActivation}
                closeActiveComponent={closeActiveComponent}
                closeActiveSegmentComponent={closeActiveSegmentComponent}
                handleSegment0ClickAway={handleSegment0ClickAway}
                createSegmentClickAwayHandler={createSegmentClickAwayHandler}
                getMinDateForSegment={getMinDateForSegment}
                handleTravelerChange={handleTravelerChange}
                handleFlightClassChange={handleFlightClassChange}
                canIncreaseAdults={canIncreaseAdults(travelersState)}
                canIncreaseChildren={canIncreaseChildren(travelersState)}
                canIncreaseInfants={canIncreaseInfants(travelersState)}
                canDecreaseAdults={canDecreaseAdults(travelersState)}
                canDecreaseChildren={canDecreaseChildren(travelersState)}
                canDecreaseInfants={canDecreaseInfants(travelersState)}
                t={t}
            />
    )
}

export { DesktopMultiCitySearchFormWrapper }
export default DesktopMultiCitySearchFormWrapper

